import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gavel, Check, ChevronLeft, ChevronRight, Users, Music, Sparkles, Heart, Star, Send, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";

// Slider-based score control for mobile-friendly decimal scoring
const ScoreSlider = ({ label, icon, color, value, onChange }) => {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
            {icon}
          </div>
          <span className="text-white font-medium text-sm">{label}</span>
        </div>
        <span 
          className="font-['Playfair_Display'] text-2xl font-bold min-w-[3rem] text-right"
          style={{ color }}
        >
          {value.toFixed(1)}
        </span>
      </div>
      <div className="px-1">
        <Slider
          value={[value]}
          onValueChange={([v]) => onChange(Math.round(v * 10) / 10)}
          min={5}
          max={10}
          step={0.1}
          className="touch-manipulation [&_[role=slider]]:h-7 [&_[role=slider]]:w-7 [&_[role=slider]]:border-2"
          style={{
            '--slider-color': color,
          }}
          data-testid={`slider-${label.toLowerCase()}`}
        />
        <div className="flex justify-between mt-1">
          <span className="text-[#52525B] text-[10px]">5</span>
          <span className="text-[#52525B] text-[10px]">7.5</span>
          <span className="text-[#52525B] text-[10px]">10</span>
        </div>
      </div>
    </div>
  );
};

// Dancer card component
// Criteria configs
const DEFAULT_CRITERIA_CONFIG = {
  overall: { label: "Overall", icon: <Star className="w-4 h-4" style={{ color: "#FFD700" }} />, color: "#FFD700" },
  connection: { label: "Connection", icon: <Heart className="w-4 h-4" style={{ color: "#FF007F" }} />, color: "#FF007F" },
  technique: { label: "Technique", icon: <Sparkles className="w-4 h-4" style={{ color: "#00E5FF" }} />, color: "#00E5FF" },
  musicality: { label: "Musicality", icon: <Music className="w-4 h-4" style={{ color: "#FFD700" }} />, color: "#FFD700" },
  presence: { label: "Presence", icon: <Star className="w-4 h-4" style={{ color: "#A855F7" }} />, color: "#A855F7" }
};

const CUSTOM_COLORS = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD"];

const getCriteriaConfig = (name) => {
  if (DEFAULT_CRITERIA_CONFIG[name]) return DEFAULT_CRITERIA_CONFIG[name];
  const idx = name.length % CUSTOM_COLORS.length;
  const color = CUSTOM_COLORS[idx];
  return { label: name.charAt(0).toUpperCase() + name.slice(1), icon: <Star className="w-4 h-4" style={{ color }} />, color };
};

const DancerCard = ({ dancer, scores, onScoreChange, isExpanded, onToggle, roundConfig }) => {
  const isLeader = dancer.role === "Leader";
  const borderColor = isLeader ? "#FFD700" : "#FF007F";
  const criteria = roundConfig?.criteria || ["overall"];
  const avgScore = criteria.length > 0
    ? criteria.reduce((sum, c) => sum + (scores?.[c] || 5), 0) / criteria.length
    : 0;

  return (
    <motion.div
      className={`bg-gradient-to-b from-[#1a1a1f] to-[#111113] rounded-2xl overflow-hidden border transition-all shadow-lg ${isExpanded ? 'ring-2 ring-white/20 border-white/10' : 'border-white/5'}`}
      layout
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-5 flex items-center justify-between text-left touch-manipulation"
        data-testid={`dancer-toggle-${dancer.dancer_id}`}
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold shadow-inner"
            style={{ backgroundColor: `${borderColor}15`, color: borderColor, border: `2px solid ${borderColor}40` }}
          >
            #{dancer.bib_number || '?'}
          </div>
          <div>
            <p className="text-white font-['Playfair_Display'] font-bold text-lg">{dancer.name}</p>
            <p className="text-sm font-medium" style={{ color: borderColor }}>{dancer.role}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[#FFD700] font-['Playfair_Display'] text-2xl font-bold">{avgScore.toFixed(1)}</p>
          <p className="text-[#52525B] text-xs uppercase tracking-wider">avg</p>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-5 pb-5 border-t border-white/5"
          >
            <div className="pt-4 space-y-1">
            {criteria.map(criterion => {
              const config = getCriteriaConfig(criterion);
              return (
                <ScoreSlider
                  key={criterion}
                  label={config.label}
                  icon={<div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>{config.icon}</div>}
                  color={config.color}
                  value={scores?.[criterion] || 5}
                  onChange={(v) => onScoreChange(criterion, v)}
                />
              );
            })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function JudgeScoringPage() {
  const { judgeId } = useParams();
  const navigate = useNavigate();
  const [judge, setJudge] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [heats, setHeats] = useState([]);
  const [currentHeatIndex, setCurrentHeatIndex] = useState(0);
  const [dancers, setDancers] = useState([]);
  const [scores, setScores] = useState({});
  const [expandedDancer, setExpandedDancer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [existingScores, setExistingScores] = useState({});
  const [roundConfig, setRoundConfig] = useState({ scoring_mode: "single", judging_mode: "full", criteria: ["overall"], score_min: 5, score_max: 10 });
  const retryRef = useRef(null);

  const currentHeat = heats[currentHeatIndex];

  const fetchData = useCallback(async () => {
    try {
      // Get judge info
      const judgeRes = await axios.get(`${API}/judges/${judgeId}`);
      setJudge(judgeRes.data);

      // Get competition
      const compRes = await axios.get(`${API}/competitions/${judgeRes.data.competition_id}`);
      setCompetition(compRes.data);

      // Get round scoring config
      const currentRound = compRes.data.current_round || 1;
      try {
        const configRes = await axios.get(`${API}/competitions/${judgeRes.data.competition_id}/round-config/${currentRound}`);
        setRoundConfig(configRes.data);
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error("Round config not found, using default:", e.message); }

      // Get heats for current round
      const heatsRes = await axios.get(`${API}/heats?competition_id=${judgeRes.data.competition_id}&round_number=${currentRound}`);
      setHeats(heatsRes.data);

      // Find active heat
      const activeIndex = heatsRes.data.findIndex(h => h.status === "active");
      if (activeIndex !== -1) {
        setCurrentHeatIndex(activeIndex);
      }
      setConnecting(false);
      clearInterval(retryRef.current);
    } catch (error) {
      // If network error (backend sleeping), show connecting screen and retry
      if (!error.response) {
        setConnecting(true);
        return; // Don't set loading=false, keep retrying
      }
      if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [judgeId]);

  useEffect(() => {
    fetchData();
    // Auto-retry every 3s if backend is sleeping
    retryRef.current = setInterval(() => {
      if (connecting) fetchData();
    }, 3000);
    return () => clearInterval(retryRef.current);
  }, [fetchData, connecting]);

  const fetchHeatDancers = useCallback(async () => {
    if (!currentHeat) return;
    
    try {
      const res = await axios.get(`${API}/heats/${currentHeat.heat_id}/dancers`);
      let heatDancers = res.data.dancers;
      
      // Filter dancers by judge role when role-based judging is active
      if (roundConfig.judging_mode === "role_based" && judge?.judge_role) {
        heatDancers = heatDancers.filter(d => d.role === judge.judge_role);
      }
      
      setDancers(heatDancers);

      // Fetch existing scores for this heat
      const scoresRes = await axios.get(`${API}/scores/judge/${judgeId}/heat/${currentHeat.heat_id}`);
      const existingMap = {};
      scoresRes.data.forEach(score => {
        existingMap[score.dancer_id] = score;
      });
      setExistingScores(existingMap);

      // Initialize scores - ALWAYS start at 5 for fresh scoring
      // Only restore saved scores if judge already submitted for THIS SPECIFIC heat
      const initialScores = {};
      heatDancers.forEach(dancer => {
        const existing = existingMap[dancer.dancer_id];
        if (existing?.criteria_scores && Object.keys(existing.criteria_scores).length > 0 && existing.heat_id === currentHeat.heat_id) {
          // Judge already scored this dancer in THIS heat - restore
          initialScores[dancer.dancer_id] = { ...existing.criteria_scores };
        } else {
          // Fresh: all criteria start at 5
          const defaults = {};
          (roundConfig.criteria || ["overall"]).forEach(c => { defaults[c] = 5; });
          initialScores[dancer.dancer_id] = defaults;
        }
      });
      setScores(initialScores);

      // Auto-expand first dancer if none expanded
      if (heatDancers.length > 0 && !expandedDancer) {
        setExpandedDancer(heatDancers[0].dancer_id);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching heat dancers:", error);
    }
  }, [currentHeat, judgeId, expandedDancer, roundConfig, judge]);

  useEffect(() => {
    if (!connecting) {
      const interval = setInterval(fetchData, 10000); // Refresh every 10s
      return () => clearInterval(interval);
    }
  }, [fetchData, connecting]);

  useEffect(() => {
    if (currentHeat) {
      fetchHeatDancers();
    }
  }, [currentHeat?.heat_id]);

  const updateScore = (dancerId, category, value) => {
    setScores(prev => ({
      ...prev,
      [dancerId]: {
        ...prev[dancerId],
        [category]: value
      }
    }));
  };

  const handleSubmitAll = async () => {
    if (!currentHeat || dancers.length === 0) return;

    setSubmitting(true);
    try {
      // Submit scores for all dancers in parallel
      const promises = dancers.map(dancer => 
        axios.post(`${API}/scores?judge_id=${judgeId}`, {
          heat_id: currentHeat.heat_id,
          dancer_id: dancer.dancer_id,
          criteria_scores: scores[dancer.dancer_id] || {}
        })
      );

      await Promise.all(promises);
      toast.success(`Scores submitted for Heat ${currentHeat.heat_number}!`);

      // Move to next heat if available
      if (currentHeatIndex < heats.length - 1) {
        setCurrentHeatIndex(currentHeatIndex + 1);
        setExpandedDancer(null);
      } else {
        toast.success("All heats scored for this round!");
      }
    } catch (error) {
      toast.error("Failed to submit scores");
    } finally {
      setSubmitting(false);
    }
  };

  const goToPrevHeat = () => {
    if (currentHeatIndex > 0) {
      setCurrentHeatIndex(currentHeatIndex - 1);
      setExpandedDancer(null);
    }
  };

  const goToNextHeat = () => {
    if (currentHeatIndex < heats.length - 1) {
      setCurrentHeatIndex(currentHeatIndex + 1);
      setExpandedDancer(null);
    }
  };

  if (loading || connecting) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#FFD700]/10 flex items-center justify-center mx-auto mb-4">
            <Gavel className="w-8 h-8 text-[#FFD700]" />
          </div>
          <div className="w-10 h-10 border-3 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white font-bold mb-1">{connecting ? "Connecting..." : "Loading..."}</p>
          {connecting && <p className="text-[#A1A1AA] text-sm">Please wait a moment</p>}
        </div>
      </div>
    );
  }

  if (!judge) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center">
          <Gavel className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
          <h1 className="text-xl text-white mb-2">Session Expired</h1>
          <p className="text-[#A1A1AA] mb-6">Please log in again</p>
          <Button onClick={() => navigate("/judge-login")} className="btn-gold">
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col">
      {/* Header */}
      <header className="glass px-4 py-3 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/jj-logo.png" alt="J&J" className="w-8 h-8" />
            <Gavel className="w-5 h-5 text-[#FFD700]" />
            <span className="text-white font-medium text-sm">{judge.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[#A1A1AA] text-xs">{competition?.category}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="text-[#52525B] hover:text-white p-1"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 pb-32 overflow-y-auto">
        {!currentHeat ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#A1A1AA]">Waiting for heat to start...</p>
              <p className="text-[#52525B] text-sm mt-2">The organizer will activate the next heat</p>
            </div>
          </div>
        ) : currentHeat.status !== "active" ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-[#FFD700]/10 flex items-center justify-center mx-auto mb-6">
                <Gavel className="w-10 h-10 text-[#FFD700]" />
              </div>
              <h2 className="text-white font-['Playfair_Display'] text-2xl font-bold mb-2">Heat {currentHeat.heat_number}</h2>
              <p className="text-[#A1A1AA] mb-2">Waiting for organizer to start this heat</p>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/10 rounded-full border border-[#F59E0B]/20">
                <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse"></span>
                <span className="text-[#F59E0B] text-sm font-medium">Standby</span>
              </span>
            </div>
          </div>
        ) : (
          <>
            {/* Heat Navigation with logos */}
            <div className="flex items-center justify-between mb-4">
              <img src="/jj-logo.png" alt="J&J" className="w-10 h-10 opacity-70" />
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF007F]/20 rounded-full">
                  <span className="w-2 h-2 bg-[#FF007F] rounded-full animate-pulse"></span>
                  <span className="text-[#FF007F] text-sm font-bold">HEAT {currentHeat.heat_number} / {heats.length}</span>
                </div>
                <p className="text-[#52525B] text-xs mt-1">{dancers.length} dancers</p>
              </div>
              <div className="w-10 h-10" />
            </div>

            {/* Dancers List - Grouped by Role */}
            {(() => {
              const leaderDancers = dancers.filter(d => d.role === "Leader").sort((a, b) => (a.bib_number || 0) - (b.bib_number || 0));
              const followerDancers = dancers.filter(d => d.role === "Follower").sort((a, b) => (a.bib_number || 0) - (b.bib_number || 0));
              return (
                <div className="space-y-4">
                  {leaderDancers.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-[#FFD700]"></div>
                        <span className="text-[#FFD700] text-xs font-bold uppercase tracking-wider">Leaders ({leaderDancers.length})</span>
                      </div>
                      <div className="space-y-3">
                        {leaderDancers.map((dancer) => (
                          <DancerCard
                            key={dancer.dancer_id}
                            dancer={dancer}
                            scores={scores[dancer.dancer_id] || {}}
                            onScoreChange={(category, value) => updateScore(dancer.dancer_id, category, value)}
                            isExpanded={expandedDancer === dancer.dancer_id}
                            onToggle={() => setExpandedDancer(expandedDancer === dancer.dancer_id ? null : dancer.dancer_id)}
                            roundConfig={roundConfig}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {followerDancers.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2 mt-4">
                        <div className="w-3 h-3 rounded-full bg-[#FF007F]"></div>
                        <span className="text-[#FF007F] text-xs font-bold uppercase tracking-wider">Followers ({followerDancers.length})</span>
                      </div>
                      <div className="space-y-3">
                        {followerDancers.map((dancer) => (
                          <DancerCard
                            key={dancer.dancer_id}
                            dancer={dancer}
                            scores={scores[dancer.dancer_id] || {}}
                            onScoreChange={(category, value) => updateScore(dancer.dancer_id, category, value)}
                            isExpanded={expandedDancer === dancer.dancer_id}
                            onToggle={() => setExpandedDancer(expandedDancer === dancer.dancer_id ? null : dancer.dancer_id)}
                            roundConfig={roundConfig}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {dancers.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-[#27272a] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">No dancers in this heat</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Fixed Bottom Action Bar - only when heat is active */}
      {currentHeat && currentHeat.status === "active" && dancers.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 glass-heavy p-4 border-t border-white/10 safe-area-bottom">
          <Button
            className="w-full btn-gold h-14 text-lg font-bold"
            onClick={handleSubmitAll}
            disabled={submitting}
            data-testid="submit-all-btn"
          >
            {submitting ? (
              <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Heat {currentHeat.heat_number} Scores
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
